import { createElement, useMemo, type ElementType } from "react";
import { useCustomerNavSectionsV2 } from "routes/Customer/customerNavSectionsV2";

import type { NavSection } from "../LeftNavV2/LeftNavDrawerV2";

import { type Command, useRegisterCommands } from "../CommandPaletteRegistry";
import { getNavItemUrl } from "../LeftNavV2/NavItemV2";

const BREADCRUMB_SEPARATOR = " › ";

/**
 * The subset of a sidebar nav item a palette command needs. Group rows are
 * adapted into this shape too, so one converter covers every navigable row.
 */
type SidebarCommandSource = {
  text: string;
  path: string;
  isExternal?: boolean;
  isDisabled?: boolean;
  onSelect?: () => void;
  Icon?: ElementType;
};

function toCommand(
  item: SidebarCommandSource,
  breadcrumb: string,
  navigate: (path: string) => void
): Command | null {
  if (item.isDisabled) {
    return null;
  }

  const url = getNavItemUrl(item);
  const { Icon } = item;

  return {
    id: `sidebar-nav:${breadcrumb}:${item.text}`,
    label: `Go to ${item.text}`,
    description: breadcrumb,
    type: "goto",
    icon: Icon ? createElement(Icon) : undefined,
    keywords: [...breadcrumb.split(BREADCRUMB_SEPARATOR), item.text, url].map((keyword) =>
      keyword.toLowerCase()
    ),
    signature: `sidebar-nav:${url}:${item.text}:${breadcrumb}`,
    run: () => {
      item.onSelect?.();
      if (item.isExternal) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(url);
    },
  };
}

function appendCommand(commands: Command[], command: Command | null) {
  if (command) {
    commands.push(command);
  }
}

/**
 * Flatten the sidebar's sections into palette commands. The sections passed in
 * are the exact output the drawer renders, so all permission / feature-flag /
 * partner gating is already applied — hidden items never reach this function.
 *
 * Mirrors sidebar click behavior: an expandable group row navigates to its
 * first child (so only the children get commands), a non-expandable group row
 * navigates to its own path (so it gets one command).
 */
export function buildSidebarNavCommands(
  sections: NavSection[],
  navigate: (path: string) => void
): Command[] {
  const commands: Command[] = [];

  for (const section of sections) {
    for (const item of section.items) {
      if (item.kind === "Group") {
        // Default isExpandable to true to match GroupNavRow's behavior
        const isExpandable = item.isExpandable ?? true;
        if (isExpandable && item.children.length > 0) {
          const breadcrumb = `${section.title}${BREADCRUMB_SEPARATOR}${item.text}`;
          for (const child of item.children) {
            appendCommand(commands, toCommand(child, breadcrumb, navigate));
          }
        } else {
          appendCommand(commands, toCommand(item, section.title, navigate));
        }
        continue;
      }

      const Icon = item.kind === "Standard" ? item.Icon : undefined;
      appendCommand(commands, toCommand({ ...item, Icon }, section.title, navigate));
    }
  }

  return commands;
}

/**
 * Registers one palette command per visible sidebar destination. Mounted
 * inside the staff-only palette provider; the sections hook applies all
 * permission and feature-flag gating, so the palette mirrors the sidebar
 * exactly — including while staff emulate a customer.
 */
export function SidebarNavCommandRegistrations({
  navigate,
}: {
  navigate: (path: string) => void;
}): null {
  const { navItems } = useCustomerNavSectionsV2();
  const commands = useMemo(() => buildSidebarNavCommands(navItems, navigate), [navItems, navigate]);

  useRegisterCommands(commands, [commands], "sidebar-nav");

  return null;
}
